import React, { useState } from 'react';
import { useError } from '../contexts/ErrorContext';
import { fetchWaypointsUsage } from '../services/reportsService';
import ReportChart from './ReportChart';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import './WaypointsUsageReport.css';

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

const EVENT_LABELS = {
  WaypointCreated: 'Created',
  WaypointRemoved: 'Removed',
  WaypointNameSet: 'Renamed'
};

const WaypointsUsageReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = async () => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const result = await fetchWaypointsUsage(startDate, endDate, addError);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch waypoints usage data.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') fetchData();
  };

  // Transform dailyByType into multi-series for stacked/multi-line chart
  const buildDailyEventsSeries = () => {
    if (!data?.dailyByType?.length) return [];

    // Group by event_name
    const byType = {};
    for (const row of data.dailyByType) {
      if (!byType[row.event_name]) byType[row.event_name] = [];
      byType[row.event_name].push({ date: row.date, value: row.event_count });
    }

    return Object.entries(byType).map(([eventName, points]) => ({
      series: EVENT_LABELS[eventName] || eventName,
      data: points
    }));
  };

  // Transform dailyUsers into single-series timeseries
  const buildDailyUsersSeries = () => {
    if (!data?.dailyUsers?.length) return [];
    return data.dailyUsers.map(row => ({ date: row.date, value: row.unique_users }));
  };

  return (
    <div className="wu-report">
      <div className="wu-header">
        <div className="wu-header-left">
          <div>
            <h1 className="wu-title">Waypoints Usage</h1>
            <p className="wu-subtitle">
              Waypoint creation, removal, and rename events over time
            </p>
          </div>
        </div>
        <div className="wu-controls">
          <input
            className="wu-date-input"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <span className="wu-date-sep">to</span>
          <input
            className="wu-date-input"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="wu-fetch-btn"
            onClick={fetchData}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {error && <div className="wu-error">{error}</div>}

      {loading && (
        <div className="wu-loading">
          <div className="wu-spinner" />
          Querying BigQuery...
        </div>
      )}

      {!loading && !data && !error && (
        <div className="wu-empty">Select a date range and click Run Report.</div>
      )}

      {!loading && data && (
        <div className="wu-content">
          {/* Summary cards */}
          <div className="wu-summary">
            <div className="wu-card">
              <span className="wu-card-value">
                {data.summary.total_events.toLocaleString()}
              </span>
              <span className="wu-card-label">Total Events</span>
            </div>
            <div className="wu-card">
              <span className="wu-card-value">
                {data.summary.total_unique_users.toLocaleString()}
              </span>
              <span className="wu-card-label">Unique Users</span>
            </div>
            <div className="wu-card">
              <span className="wu-card-value">
                {data.summary.created_count.toLocaleString()}
              </span>
              <span className="wu-card-label">Created</span>
            </div>
            <div className="wu-card">
              <span className="wu-card-value">
                {data.summary.removed_count.toLocaleString()}
              </span>
              <span className="wu-card-label">Removed</span>
            </div>
            <div className="wu-card">
              <span className="wu-card-value">
                {data.summary.name_set_count.toLocaleString()}
              </span>
              <span className="wu-card-label">Renamed</span>
            </div>
          </div>

          {/* Daily waypoint events chart (multi-series) */}
          {data.dailyByType?.length > 0 && (
            <div className="wu-chart-section">
              <h3 className="wu-section-title">Daily Waypoint Events</h3>
              <div className="wu-chart-container">
                <ReportChart
                  id="waypoints-daily-events"
                  name="Daily Waypoint Events"
                  type="timeseries"
                  data={buildDailyEventsSeries()}
                />
              </div>
            </div>
          )}

          {/* Daily unique users chart */}
          {data.dailyUsers?.length > 0 && (
            <div className="wu-chart-section">
              <h3 className="wu-section-title">Daily Unique Users</h3>
              <div className="wu-chart-container">
                <ReportChart
                  id="waypoints-daily-users"
                  name="Daily Unique Users"
                  type="timeseries"
                  data={buildDailyUsersSeries()}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WaypointsUsageReport;
